/**
 * Shared Google tag bootstrap for Analytics 4 and Google Ads.
 *
 * No browser or non-production domain means no tracking. GA4 events also
 * require `VITE_GA_MEASUREMENT_ID`; Ads remains enabled on production when
 * that optional GA4 destination is absent.
 *
 * Consent Mode v2: defaults to all-denied. `updateConsent(true|false)` flips
 * every category (analytics_storage, ad_storage, ad_user_data, ad_personalization)
 * based on the single binary toggle in `CookieBanner`.
 */
import { isProductionDomain } from "@/lib/domain";

const COOKIE_STORAGE_KEY = "fluxabee_cookie_consent";
const ADS_ID = "AW-18336965373";

type DataLayerItem = IArguments | unknown[] | Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: DataLayerItem[];
    gtag?: (...args: unknown[]) => void;
  }
}

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as
  | string
  | undefined;

let initialized = false;

const isTagEnabled = (): boolean => {
  if (typeof window === "undefined") return false;
  if (!isProductionDomain) return false;
  return true;
};

const isAnalyticsEnabled = (): boolean => isTagEnabled() && !!MEASUREMENT_ID;

// gtag.js distinguishes "commands" (arguments-like objects pushed by the
// gtag() function) from "GTM events" (plain arrays/objects). Pushing arrays
// directly bypasses command processing - events land in dataLayer but never
// fire hits. So we define a real gtag() function and use `arguments`.
const ensureGtag = (): ((...args: unknown[]) => void) => {
  if (!window.dataLayer) window.dataLayer = [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }
  return window.gtag;
};

const consentStateForCurrentChoice = (): "granted" | "denied" => {
  if (typeof window === "undefined") return "denied";
  try {
    const v = window.localStorage.getItem(COOKIE_STORAGE_KEY);
    return v === "accepted" ? "granted" : "denied";
  } catch {
    return "denied";
  }
};

/**
 * Idempotently load gtag.js, bootstrap the dataLayer and send the initial
 * consent defaults + config. Safe to call multiple times.
 */
export const initAnalytics = (): void => {
  if (!isTagEnabled()) return;
  if (initialized) return;
  initialized = true;

  const gtag = ensureGtag();

  // Consent defaults MUST be pushed before `config` so the first collect
  // call carries the right `gcs=` signal.
  const state = consentStateForCurrentChoice();
  gtag("consent", "default", {
    analytics_storage: state,
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
  });

  gtag("js", new Date());
  if (MEASUREMENT_ID) {
    gtag("config", MEASUREMENT_ID, {
      anonymize_ip: true,
      send_page_view: false,
    });
  }
  gtag("config", ADS_ID);

  // Both destinations share one bootstrap. Consent defaults must precede
  // Ads as well as GA4, including a visitor's first production page load.
  const existing = document.querySelector<HTMLScriptElement>(
    'script[src^="https://www.googletagmanager.com/gtag/js"]',
  );
  if (!existing) {
    const tagId = MEASUREMENT_ID || ADS_ID;
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${tagId}`;
    script.dataset.googleTagId = tagId;
    document.head.appendChild(script);
  }
};

/**
 * Emit a GA4 `page_view` for SPA route changes. Called from `usePageviews`.
 */
export const trackPageview = (
  path: string,
  title: string,
  language: string,
): void => {
  if (!isAnalyticsEnabled()) return;
  const gtag = ensureGtag();
  gtag("event", "page_view", {
    page_path: path,
    page_title: title,
    language,
  });
};

/**
 * Emit an arbitrary GA4 event. Params are passed through untouched.
 */
export const trackEvent = (
  name: string,
  params: Record<string, unknown> = {},
): void => {
  if (!isAnalyticsEnabled()) return;
  const gtag = ensureGtag();
  gtag("event", name, params);
};

/**
 * Flip Consent Mode v2 categories on/off based on the single binary toggle
 * in CookieBanner. Safe to call before `initAnalytics` - it just lands in
 * `dataLayer` and is consumed when gtag.js loads.
 */
export const updateConsent = (granted: boolean): void => {
  if (!isTagEnabled()) return;
  const gtag = ensureGtag();
  const value = granted ? "granted" : "denied";
  gtag("consent", "update", {
    analytics_storage: value,
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  });
};

/**
 * Test-only: reset module state so analytics.test.ts can exercise a fresh
 * init each case. Not exported from the public surface.
 */
export const __resetAnalyticsForTests = (): void => {
  initialized = false;
  if (typeof window !== "undefined") {
    window.dataLayer = [];
  }
};
